import { api, type StoredFile } from "./api";

/** Mirrors the API's limits so people get an instant answer. The API and S3 enforce them too. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

export function uploadProblem(file: File, allowed: readonly string[]): string | null {
  if (!allowed.includes(file.type)) return "Upload a PDF or Word document.";
  if (file.size === 0) return "That file is empty.";
  if (file.size > MAX_UPLOAD_BYTES) return "Files can be up to 10 MB.";
  return null;
}

/**
 * Uploads straight to S3: the API presigns a POST pinned to this file's key, type and size,
 * the browser sends the file, then the API confirms S3 holds it.
 */
export async function uploadFile(file: File): Promise<StoredFile> {
  const { file: record, upload } = await api.createUpload({
    filename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });
  const form = new FormData();
  for (const [key, value] of Object.entries(upload.fields)) form.append(key, value);
  form.append("file", file); // must be the last field
  const res = await fetch(upload.url, { method: "POST", body: form });
  if (!res.ok) throw new Error("The upload didn't go through. Try again.");
  return (await api.completeUpload(record.id)).file;
}

/** Opens a short-lived download link for a stored file. */
export async function openDownload(fileId: string): Promise<void> {
  const { url } = await api.downloadUrl(fileId);
  window.location.assign(url);
}
